import {
  demoAuditEvents,
  demoInvitations,
  demoOpportunities,
  demoOpportunityUpdates,
} from "./demo-data";
import { isDemoMode, usesLocalData } from "./demo-mode";
import {
  mockAuditEvents,
  mockInvitations,
  mockOpportunities,
  mockOpportunityUpdates,
} from "./mock-data";
import type {
  AuditEvent,
  DashboardSummary,
  Invitation,
  Attachment,
  Opportunity,
  OpportunityUpdate,
  OpportunityUpdateInput,
  ReminderSettings,
} from "../types";

const useMock = usesLocalData;
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api";
let mockReminderSettings: ReminderSettings = {
  weekday: "Monday",
  hour: 10,
  timezone: "Asia/Hong_Kong",
};

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function request<T>(
  token: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...headers(token), ...init?.headers },
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json() as Promise<T>;
}

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(body?.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function acceptInvitation(input: {
  token: string;
  displayName: string;
  password: string;
}): Promise<{ email: string }> {
  if (useMock) return { email: "partner@example.com" };
  return publicRequest("/invitations/accept", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getOpportunities(token: string): Promise<Opportunity[]> {
  if (useMock) {
    return structuredClone(isDemoMode ? demoOpportunities : mockOpportunities);
  }
  return request(token, "/opportunities");
}

export async function updateOpportunity(
  token: string,
  id: string,
  input: OpportunityUpdateInput,
): Promise<Opportunity> {
  if (useMock) {
    const opportunities = isDemoMode ? demoOpportunities : mockOpportunities;
    const opportunity = opportunities.find((item) => item.id === id);
    if (!opportunity) throw new Error("Opportunity not found");
    const previousStatus = opportunity.status;
    const attachments = (input.files ?? []).map<Attachment>((file) => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      downloadUrl: URL.createObjectURL(file),
    }));
    Object.assign(opportunity, {
      status: input.status,
      nextCheckInOn: input.nextCheckInOn,
      reviewOn: input.reviewOn,
      lastUpdateNote: input.note || input.reason,
      isOverdue: false,
      updatedAt: new Date().toISOString(),
    });
    if (isDemoMode) {
      const update: OpportunityUpdate = {
        id: crypto.randomUUID(),
        opportunityId: id,
        occurredAt: new Date().toISOString(),
        actorName: "ESAC Demo",
        previousStatus,
        status: input.status,
        nextCheckInOn: input.nextCheckInOn,
        reviewOn: input.reviewOn,
        note: input.note,
        reason: input.reason,
        attachments,
      };
      demoOpportunityUpdates[id] = [
        update,
        ...(demoOpportunityUpdates[id] ?? []),
      ];
    }
    return structuredClone(opportunity);
  }
  const attachments = input.files?.length
    ? await uploadAttachments(token, id, input.files)
    : (input.attachments ?? []);
  const payload = { ...input, files: undefined };
  return request(token, `/opportunities/${id}/updates`, {
    method: "POST",
    body: JSON.stringify({ ...payload, attachments }),
  });
}

async function uploadAttachments(
  token: string,
  opportunityId: string,
  files: File[],
): Promise<Attachment[]> {
  const response = await request<{
    uploads: Array<{ attachment: Attachment; uploadUrl: string }>;
  }>(token, `/opportunities/${opportunityId}/attachments/presign`, {
    method: "POST",
    body: JSON.stringify({
      files: files.map((file) => ({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      })),
    }),
  });
  await Promise.all(
    response.uploads.map(async ({ uploadUrl }, index) => {
      const upload = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": files[index].type },
        body: files[index],
      });
      if (!upload.ok)
        throw new Error(`Attachment upload failed: ${upload.status}`);
    }),
  );
  return response.uploads.map(({ attachment }) => attachment);
}

export async function getOpportunityHistory(
  token: string,
  opportunityId: string,
): Promise<OpportunityUpdate[]> {
  if (useMock) {
    return structuredClone(
      isDemoMode
        ? (demoOpportunityUpdates[opportunityId] ?? [])
        : (mockOpportunityUpdates[opportunityId] ?? []),
    );
  }
  return request(token, `/opportunities/${opportunityId}/history`);
}

export async function getSummary(token: string): Promise<DashboardSummary> {
  if (useMock) {
    const items = isDemoMode ? demoOpportunities : mockOpportunities;
    const confirmedStatuses = new Set([
      "Paid - delivery pending",
      "Delivery in progress",
      "Live - post-launch support",
      "Completed",
    ]);
    const today = new Date().toISOString().slice(0, 10);
    const dueThrough = new Date(Date.now() + 6 * 86_400_000)
      .toISOString()
      .slice(0, 10);
    return {
      totalActive: items.filter(
        (item) =>
          !["Completed", "Closed - not proceeding"].includes(item.status),
      ).length,
      overdue: items.filter((item) => item.isOverdue).length,
      dueThisWeek: items.filter(
        (item) =>
          !["Completed", "Closed - not proceeding"].includes(item.status) &&
          item.nextCheckInOn >= today &&
          item.nextCheckInOn <= dueThrough,
      ).length,
      confirmed: items.filter((item) => confirmedStatuses.has(item.status))
        .length,
    };
  }
  return request(token, "/dashboard/summary");
}

export async function getInvitations(token: string): Promise<Invitation[]> {
  if (useMock) {
    return structuredClone(isDemoMode ? demoInvitations : mockInvitations);
  }
  return request(token, "/admin/invitations");
}

export async function getAuditEvents(token: string): Promise<AuditEvent[]> {
  if (useMock) {
    return structuredClone(isDemoMode ? demoAuditEvents : mockAuditEvents);
  }
  return request(token, "/admin/audit-events");
}

export async function getReminderSettings(
  token: string,
): Promise<ReminderSettings> {
  if (useMock) return structuredClone(mockReminderSettings);
  return request(token, "/admin/reminder-settings");
}

export async function updateReminderSettings(
  token: string,
  settings: ReminderSettings,
): Promise<ReminderSettings> {
  if (useMock) {
    mockReminderSettings = structuredClone(settings);
    return structuredClone(mockReminderSettings);
  }
  return request(token, "/admin/reminder-settings", {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

export async function importOpportunitiesCsv(
  token: string,
  file: File,
): Promise<number> {
  if (useMock) return (await file.text()).trim().split("\n").length - 1;
  const response = await fetch(`${baseUrl}/admin/import`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "text/csv" },
    body: file,
  });
  if (!response.ok) throw new Error(`CSV import failed: ${response.status}`);
  return ((await response.json()) as { imported: number }).imported;
}

export async function createInvitation(
  token: string,
  email: string,
): Promise<{ invitation: Invitation; invitationLink: string }> {
  if (useMock) {
    const invitation: Invitation = {
      id: crypto.randomUUID(),
      email,
      supplierName: "ESAC",
      expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
      status: "pending",
    };
    return {
      invitation,
      invitationLink: `https://partners.bridgeaigaming.com/accept-invitation?token=mock`,
    };
  }
  return request(token, "/admin/invitations", {
    method: "POST",
    body: JSON.stringify({
      supplierOrganizationId: "00000000-0000-0000-0000-000000000001",
      email,
    }),
  });
}

export async function createOpportunity(
  token: string,
  input: {
    clientDisplayName: string;
    market: string;
    projectName: string;
    projectSummary: string;
    nextCheckInOn: string;
  },
): Promise<void> {
  if (useMock) return;
  await request(token, "/admin/opportunities", {
    method: "POST",
    body: JSON.stringify({
      ...input,
      supplierOrganizationId: "00000000-0000-0000-0000-000000000001",
      status: "Introduced",
      assignedOn: new Date().toISOString().slice(0, 10),
    }),
  });
}
